// src/components/BlogsPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { formatBlogPublishDate, getBlogPublishTimestamp } from '../utils/blogPublishDate';
import { useTheme } from '../contexts/ThemeContext';
import PageSEO from './PageSEO';
import './BlogsPage.css';

const BLOGS_PER_PAGE = 9;

const BlogsPage = ({ onClose }) => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [blogImages, setBlogImages] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchBlogs();
  }, []);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/blogs/v1/get-blog?site_id=${SITE_CONFIG.SITE_ID}`, {
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const blogsArray = Array.isArray(data) ? data : [];
        const visibleBlogs = blogsArray.filter(blog => {
          if (blog.future_post_date) {
            return new Date(blog.future_post_date) <= new Date();
          }
          return true;
        });
        const sortedBlogs = visibleBlogs.sort((a, b) => getBlogPublishTimestamp(b) - getBlogPublishTimestamp(a));
        setBlogs(sortedBlogs);
        
        // Fetch all images immediately
        sortedBlogs.forEach(blog => {
          if (blog.file_id) {
            fetchBlogImage(blog);
          }
        });
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBlogImage = async (blog) => {
    try {
      const imageResponse = await fetch(`https://tophamtechbe.com/api/treas/blogs/v1/get-image?site_id=${SITE_CONFIG.SITE_ID}&s3_blog_file_id=${blog.file_id}`, {
        headers: { 'X-API-Key': 'treas-api-key-2025' }
      });
      if (imageResponse.ok) {
        const imageData = await imageResponse.json();
        setBlogImages(prev => ({...prev, [blog.id]: imageData.url}));
      }
    } catch (error) {
      console.error('Error fetching image for blog:', blog.id, error);
    }
  };

  const paginatedBlogs = blogs.slice(0, currentPage * BLOGS_PER_PAGE);
  const hasMore = currentPage * BLOGS_PER_PAGE < blogs.length;

  return (
    <div className={`blogs-page ${isDarkMode ? 'dark-mode' : ''}`}>
      <PageSEO
        title="Real Estate Blog - Property Tips & Market Insights | David Hamady"
        description="Stay informed with our real estate blog featuring property tips, market insights, buying guides, and local real estate news from David Hamady."
        path="/blogs"
      />
      <div className="blogs-header">
        <h1>Real Estate Blog & Property Insights</h1>
        <button className="close-blogs-btn" onClick={() => navigate('/')}>
          Home
        </button>
      </div>
      
      <div className="blogs-container">
        {selectedBlog ? (
          <div className="blog-detail">
            <button 
              className="back-to-list-btn" 
              onClick={() => setSelectedBlog(null)}
            >
              ← Back to Blogs
            </button>
            <iframe
              src={selectedBlog.url}
              title={selectedBlog.title}
              className="blog-detail-iframe"
            />
          </div>
        ) : (
          <>
            <div className="blogs-grid">
              {loading ? (
                <p>Loading blogs...</p>
              ) : blogs.length === 0 ? (
                <p>No blogs available at this time.</p>
              ) : (
                paginatedBlogs.map((blog, index) => (
                  <div 
                    key={index}
                    data-blog-id={blog.id}
                    className="blog-card"
                    onClick={() => {
                      const slug = blog.title.toLowerCase()
                        .replace(/[^a-z0-9 -]/g, '')
                        .replace(/\s+/g, '-')
                        .replace(/-+/g, '-')
                        .trim('-');
                      navigate(`/blog/${slug}`);
                    }}
                    style={{
                      backgroundImage: blogImages[blog.id] ? `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.6)), url(${blogImages[blog.id]})` : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  >
                    <div style={{padding: '20px'}}>
                      <h3 style={{color: blogImages[blog.id] ? 'white' : 'inherit'}}>{blog.title}</h3>
                      <p className="blog-date" style={{color: blogImages[blog.id] ? 'rgba(255,255,255,0.8)' : 'inherit'}}>
                        {formatBlogPublishDate(blog, { month: 'short' })}
                      </p>
                      <button className="read-more-btn">Read Full Post</button>
                    </div>
                  </div>
                ))
              )}
            </div>
            {hasMore && (
              <div style={{textAlign: 'center', marginTop: '30px'}}>
                <button 
                  className="read-more-btn" 
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  style={{padding: '12px 30px', fontSize: '16px'}}
                >
                  Load More Blogs
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BlogsPage;
